import { useState, useEffect } from 'react';
import { useOrganizationSettings } from './useOrganizationSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { resetCssVariables } from '@/lib/subdomain-utils';

/**
 * Convert hex color to HSL format
 */
export const hexToHsl = (hex: string): string => {
  if (!hex || !hex.startsWith('#')) return '';
  
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number, l: number;

  l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

interface ThemeUtilsOptions {
  organizationId?: string | null;
}

/**
 * Theme utilities hook - provides helpers for reading and updating theme settings
 * Does NOT apply CSS variables - that's handled by useThemeApplicator
 */
export const useThemeUtils = (options?: ThemeUtilsOptions) => {
  const organizationId = options?.organizationId;
  
  // Global theme state
  const [globalPrimaryColor, setGlobalPrimaryColor] = useState<string>('#3dc298');
  const [globalThemeLoading, setGlobalThemeLoading] = useState(true);
  
  // Organization theme hooks
  const { getSetting, loading: orgSettingsLoading, updateSetting } = useOrganizationSettings(organizationId || undefined);
  
  // Load global theme on init
  useEffect(() => {
    const fetchGlobalTheme = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'theme_primary_color')
          .maybeSingle();

        if (error) {
          console.error('Error fetching global theme color:', error);
          return;
        }

        if (data?.value) {
          setGlobalPrimaryColor(data.value);
        }
      } catch (error) {
        console.error('Error fetching global theme color:', error);
      } finally {
        setGlobalThemeLoading(false);
      }
    };

    fetchGlobalTheme();
  }, []);
  
  // Update global theme
  const setGlobalTheme = async (color: string) => {
    try {
      const { error } = await supabase.rpc(
        'admin_update_app_setting',
        {
          p_key: 'theme_primary_color',
          p_value: color,
          p_description: 'Global primary theme color for all users'
        }
      );

      if (error) {
        throw error;
      }

      setGlobalPrimaryColor(color);
      toast.success('Theme color updated globally');
    } catch (error: any) {
      console.error('Error updating global theme color:', error);
      toast.error('Failed to update theme color: ' + error.message);
    }
  };
  
  // Update organization theme setting
  const setOrganizationTheme = async (key: string, value: string) => {
    await updateSetting(key, value);
    // React Query + useThemeApplicator will handle CSS application automatically
  };
  
  // Reset theme to defaults
  const resetTheme = () => {
    resetCssVariables();
  };

  return {
    // Global theme
    globalPrimaryColor,
    setGlobalTheme,
    globalThemeLoading,
    
    // Organization theme
    setOrganizationTheme,
    orgSettingsLoading,
    getSetting,
    
    // Utils
    hexToHsl,
    resetTheme
  };
};
